/**
 * Token Expiry Warning Hook
 *
 * Shows a toast notification only when the silent token refresh failed (non-401) and the
 * access token is still valid. Gives users time to save their work before session expires.
 *
 * Usage:
 * ```typescript
 * function AdminLayout() {
 *   useTokenExpiryWarning();
 *   return <Layout />;
 * }
 * ```
 */

import { useEffect } from 'react';
import type { RootState } from '../../../App/store';
import { toast } from 'react-toastify';
import { useAppSelector } from '../../../App/hooks';

const WARNING_ID = 'token-expiry-warning'; // Prevent duplicate toasts

export const useTokenExpiryWarning = (): void => {
  const tokenExpiresAt = useAppSelector((state: RootState) => state.auth.tokenExpiresAt);
  const isAuthenticated = useAppSelector((state: RootState) => state.auth.isAuthenticated);
  const sessionRefreshFailed = useAppSelector(
    (state: RootState) => state.auth.sessionRefreshFailed
  );

  useEffect(() => {
    const timeUntilExpiry = tokenExpiresAt ? tokenExpiresAt - Date.now() : 0;

    if (!sessionRefreshFailed || !isAuthenticated || timeUntilExpiry <= 0) {
      // Refresh recovered (or the session ended): drop a warning that is still on screen
      toast.dismiss(WARNING_ID);
      return;
    }

    const minutesLeft = Math.ceil(timeUntilExpiry / 60000);

    toast.info(
      `Sesja wygaśnie za ${minutesLeft} min${minutesLeft === 1 ? 'utę' : minutesLeft < 5 ? 'uty' : 'ut'}. Zapisz swoją pracę.`,
      {
        toastId: WARNING_ID,
        position: 'bottom-right',
        autoClose: 6000,
        hideProgressBar: true,
        closeOnClick: true,
        draggable: false,
      }
    );
  }, [tokenExpiresAt, isAuthenticated, sessionRefreshFailed]);
};
